import { useEffect, useRef, useState } from "react";

/**
 * Inline click-to-edit text.
 * - single line: <input>
 * - multi line: <textarea> (auto-grows)
 * Shows the value when not editing, focuses on click.
 * Commits on blur or Enter (Enter for input; Ctrl/Cmd+Enter for textarea).
 * Escape cancels.
 */
export const EditableText = ({
  value = "",
  onChange,
  multiline = false,
  placeholder = "Click to edit…",
  className = "",
  as: Tag = "span",
  testid,
  disabled = false,
  emptyLabel = "Add text…",
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // Move cursor to end
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
      autoresize();
    }
  }, [editing]);

  const autoresize = () => {
    if (multiline && inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = inputRef.current.scrollHeight + "px";
    }
  };

  const commit = () => {
    setEditing(false);
    if (draft !== value) onChange?.(draft);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    } else if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      commit();
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && multiline) {
      e.preventDefault();
      commit();
    }
  };

  if (editing) {
    return multiline ? (
      <textarea
        ref={inputRef}
        className={`mvp-editable-input ${className}`}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          autoresize();
        }}
        onBlur={commit}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        data-testid={testid ? `${testid}-input` : undefined}
        rows={1}
      />
    ) : (
      <input
        ref={inputRef}
        className={`mvp-editable-input ${className}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        data-testid={testid ? `${testid}-input` : undefined}
      />
    );
  }

  const isEmpty = value == null || value === "";
  return (
    <Tag
      className={`mvp-editable ${isEmpty ? "mvp-editable-empty" : ""} ${disabled ? "mvp-editable-disabled" : ""} ${className}`}
      onClick={disabled ? undefined : () => setEditing(true)}
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? undefined : 0}
      onKeyDown={disabled ? undefined : (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEditing(true); }
      }}
      title={disabled ? "" : "Click to edit"}
      data-testid={testid}
    >
      {isEmpty ? <span className="mvp-editable-placeholder">{emptyLabel}</span> : value}
    </Tag>
  );
};
